import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  CheckCircle2, Circle, Loader2, AlertTriangle, ShieldAlert,
  Sparkles, ChevronRight, Lock, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { fireMilestone, MILESTONES } from "../lib/onboardingMilestones";

const STAGES = [
  { key: "uploading",      label: "Collecting resources" },
  { key: "crawling",       label: "Crawling website" },
  { key: "building_kb",    label: "Building knowledge base" },
  { key: "generating_qa",  label: "Generating Q&A" },
  { key: "running_tests",  label: "Running validation" },
  { key: "finalizing",     label: "Finalizing AI Employee" },
  { key: "ready",          label: "Ready" },
];

function StageRow({ stage, state, isActive, waiting = false, waitingLabel = null }) {
  let Icon = Circle;
  let cls = "text-slate-400";
  let badge = null;
  if (state === "complete") { Icon = CheckCircle2; cls = "text-emerald-700"; }
  else if (waiting) {
    // Brand-new tenant — stage is the active pipeline step but no resources
    // have been added yet. Show a neutral cue instead of the "running" spinner
    // so the user understands they need to take action.
    Icon = Circle;
    cls = "text-amber-500";
    badge = <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 bg-amber-50">waiting for you</Badge>;
  }
  else if (state === "in_progress") { Icon = Loader2; cls = "text-amber-600 animate-spin"; badge = <Badge variant="secondary" className="text-[10px]">running</Badge>; }
  else if (state === "skipped") { Icon = CheckCircle2; cls = "text-slate-300"; badge = <span className="text-[10px] text-slate-400">skipped</span>; }
  return (
    <div className={`flex items-center gap-3 py-2.5 ${isActive ? "bg-emerald-50/60 rounded-md px-3 -mx-3 border-l-2 border-emerald-700" : ""}`}>
      <Icon className={`w-5 h-5 shrink-0 ${cls}`} />
      <div className="flex-1 min-w-0">
        <span className={`block text-sm ${state === "complete" ? "text-foreground" : (state === "in_progress" || waiting) ? "font-medium" : "text-muted-foreground"}`}>{stage.label}</span>
        {waiting && waitingLabel && (
          <span className="block text-[11px] text-amber-700 mt-0.5">{waitingLabel}</span>
        )}
      </div>
      {badge}
    </div>
  );
}

/**
 * ProvisioningPanel — visible on the Dashboard when the tenant is in the
 * Slice C provisioning pipeline (`requires_provisioning=true` and
 * `provisioning_status != "ready"`). Drives:
 *   - Knowledge Draft (generate / edit / approve)
 *   - Proposed Q&A (generate / approve / reject / edit)
 *   - Final activation
 *   - Red-flag display + retry path
 */
export default function ProvisioningPanel() {
  const [state, setState] = useState(null);
  const [qa, setQa] = useState({ rows: [], summary: { total: 0, approved: 0, rejected: 0, pending: 0, edited: 0 } });
  const [busy, setBusy] = useState(false);
  const [draftEdit, setDraftEdit] = useState(null);
  // Track previous state across refreshes so we can detect transitions and
  // fire the product-narrative onboarding toasts EXACTLY at the moment the
  // tenant crosses a milestone boundary.
  const prevSnapshot = useRef({ qaTotal: 0, status: null });

  const refresh = useCallback(async () => {
    try {
      const [s, q] = await Promise.all([
        api.get("/dashboard/provisioning/status").then(r => r.data),
        api.get("/dashboard/provisioning/qa").then(r => r.data).catch(() => ({ rows: [], summary: {} })),
      ]);
      // Milestone: Q&A reached the minimum-required count for the first time.
      const minQa = s?.min_qa_required || 3;
      const newQaTotal = q?.summary?.total || 0;
      if (prevSnapshot.current.qaTotal < minQa && newQaTotal >= minQa) {
        fireMilestone("qa_ready_for_review", MILESTONES.QA_READY_FOR_REVIEW);
      }
      // Milestone: provisioning_status crossed into "ready" for the first time.
      if (prevSnapshot.current.status !== "ready" && s?.provisioning_status === "ready") {
        fireMilestone("ready_to_go_live", MILESTONES.READY_TO_GO_LIVE);
      }
      prevSnapshot.current = { qaTotal: newQaTotal, status: s?.provisioning_status };
      setState(s);
      setQa(q);
      if (s?.knowledge_draft?.content_md && draftEdit === null) {
        setDraftEdit(s.knowledge_draft.content_md);
      }
    } catch (e) { /* swallow — first render may 401 if not logged in */ }
  }, [draftEdit]);

  useEffect(() => { refresh(); }, [refresh]);

  // v1.0.8 — listen for resource-changed events emitted by the Resources page
  // (upload, website import, delete). When a brand-new tenant adds their
  // first resource, this fires the status refresh so the panel transitions
  // immediately from "Waiting for resources" → "Collecting resources" /
  // "Building knowledge base" without forcing a page reload. Debounced via
  // a short timer because backend updates the pipeline state asynchronously
  // after the upload returns.
  useEffect(() => {
    const onChange = () => {
      // small delay so the backend has time to flip pipeline state after the
      // POST returns (otherwise the immediate refresh may read the old state).
      setTimeout(() => { refresh(); }, 450);
    };
    window.addEventListener("rudras:resources-changed", onChange);
    return () => window.removeEventListener("rudras:resources-changed", onChange);
  }, [refresh]);

  if (!state) return null;

  // Existing tenants: this panel is a no-op.
  if (!state.requires_provisioning || state.provisioning_status === "ready") {
    return null;
  }

  const pipeline = state.provisioning_pipeline_state || {};
  const currentStageKey = state.provisioning_status;
  const draft = state.knowledge_draft;
  const minQa = state.min_qa_required || 3;
  const approvedQa = (qa.summary?.approved || 0) + (qa.summary?.edited || 0);
  // A brand-new tenant lands on `provisioning_status="uploading"` with the
  // "uploading" stage flagged `in_progress` server-side — semantically it
  // means "this is the active step", not "a background job is running". When
  // the tenant has zero resources we surface this as "waiting for you" so
  // users aren't misled by a spinning indicator over a stage that hasn't
  // actually started any background work yet.
  const totalResources = state.resource_counts?.total ?? null;
  const waitingForResources = (
    currentStageKey === "uploading"
    && pipeline.uploading === "in_progress"
    && totalResources === 0
  );
  // Onboarding messaging (v1.0.14) — three explicit cases so we never show
  // generic "add a resource" copy when the tenant has already supplied a
  // website during registration or the last crawl just failed.
  const websiteUrl = (state.website || "").trim();
  const lastCrawl = state.last_website_crawl || null;
  const lastCrawlFailed = lastCrawl && (lastCrawl.status === "failed" || lastCrawl.status === "low_content");
  const lastCrawlOk = lastCrawl && (lastCrawl.status === "completed" || lastCrawl.status === "completed_partial");
  let waitingLabel = null;
  if (waitingForResources) {
    if (lastCrawlFailed) {
      // CASE C — crawl failed, give actionable retry copy.
      waitingLabel =
        "We could not fully access your website. Please retry, upload documents, or contact support.";
    } else if (websiteUrl && !lastCrawlOk) {
      // CASE A — website URL captured at registration, no successful crawl yet.
      waitingLabel =
        "Your website URL is ready. Click 'Import Website' to crawl your website and build your AI knowledge base.";
    } else {
      // CASE B — no website, no documents.
      waitingLabel = "Add your website URL or upload documents to begin.";
    }
  }
  const phaseLabel = waitingForResources ? "Waiting for resources" : currentStageKey;

  const callApi = async (fn) => {
    setBusy(true);
    try { await fn(); await refresh(); }
    catch (e) {
      const det = e.response?.data?.detail || e.response?.data || e.message;
      toast.error(typeof det === "string" ? det : (det?.message || "Provisioning action failed"));
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-6 lg:p-8 border-emerald-900/15 bg-gradient-to-br from-emerald-50/40 via-white to-amber-50/30 mb-8" data-testid="provisioning-panel">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-700 to-emerald-900 ring-1 ring-amber-300/30 grid place-items-center shadow">
            <Sparkles className="w-5 h-5 text-amber-300" />
          </div>
          <div className="leading-tight">
            <h2 className="font-display text-2xl font-semibold tracking-tight">Provisioning your AI Employee</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Your public link goes live after you approve the knowledge draft and Q&amp;A.</p>
          </div>
        </div>
        <Badge className="bg-emerald-900 text-amber-200 hover:bg-emerald-950" data-testid="provisioning-phase-badge">Phase: {phaseLabel}</Badge>
      </div>

      {/* Pipeline visual */}
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="rounded-xl border bg-card p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3 font-medium">Pipeline progress</div>
          {STAGES.map((s) => (
            <StageRow
              key={s.key}
              stage={s}
              state={pipeline[s.key] || "pending"}
              isActive={currentStageKey === s.key && pipeline[s.key] === "in_progress"}
              waiting={waitingForResources && s.key === "uploading"}
              waitingLabel={waitingForResources && s.key === "uploading" ? waitingLabel : null}
            />
          ))}
          {waitingForResources && (
            <div className="mt-4 pt-4 border-t border-dashed border-amber-300">
              <Link to="/dashboard/resources?focus=website" data-testid="provisioning-add-resources-cta">
                <Button
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white shadow-sm"
                  data-testid="provisioning-add-resources-btn"
                >
                  Add Resources
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <p className="text-[11px] text-muted-foreground mt-2 text-center">
                Pasting your website URL is the fastest way to onboard your AI Employee.
              </p>
            </div>
          )}
        </div>

        <div className="space-y-5">
          {state.red_flag && (
            <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 flex gap-3" data-testid="provisioning-red-flag">
              <ShieldAlert className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
              <div className="text-sm leading-snug">
                <div className="font-semibold text-red-900 mb-1">Provisioning blocked</div>
                <div className="text-red-800">{state.red_flag_reason || "Insufficient business information available."}</div>
                <div className="text-xs text-red-700 mt-2">Upload additional resources or update your website information, then click "Retry knowledge draft".</div>
              </div>
            </div>
          )}

          {/* Knowledge draft step */}
          <div className="rounded-xl border bg-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-medium tracking-tight">Step 1 — Knowledge Draft</div>
                <div className="text-xs text-muted-foreground mt-0.5">A summary of what your AI Employee will know.</div>
              </div>
              {draft?.status === "approved"
                ? <Badge className="bg-emerald-700 hover:bg-emerald-800 text-white">Approved</Badge>
                : draft ? <Badge variant="outline">Pending review</Badge>
                : <Badge variant="secondary">Not started</Badge>}
            </div>
            {!draft && (
              <Button
                disabled={busy}
                onClick={() => callApi(async () => {
                  const r = await api.post("/dashboard/provisioning/generate-knowledge-draft").then(rr => rr.data);
                  if (r?.blocked) { toast.warning(r.reason || "Insufficient information."); }
                  else { toast.success("Knowledge draft generated."); }
                })}
                className="bg-emerald-900 hover:bg-emerald-950 text-white"
                data-testid="prov-generate-draft"
              >
                Generate Knowledge Draft
              </Button>
            )}
            {draft && (
              <>
                <Textarea
                  value={draftEdit ?? draft.content_md ?? ""}
                  onChange={(e) => setDraftEdit(e.target.value)}
                  disabled={busy || draft.status === "approved"}
                  rows={8}
                  className="font-mono text-xs"
                  data-testid="prov-draft-textarea"
                />
                <div className="flex gap-2 mt-3">
                  {draft.status !== "approved" && (
                    <>
                      <Button
                        size="sm" variant="outline" disabled={busy}
                        onClick={() => callApi(async () => {
                          await api.patch("/dashboard/provisioning/knowledge-draft", { content_md: draftEdit });
                          toast.success("Draft saved.");
                        })}
                        data-testid="prov-save-draft"
                      >Save edits</Button>
                      <Button
                        size="sm" disabled={busy}
                        className="bg-emerald-900 hover:bg-emerald-950 text-white"
                        onClick={() => callApi(async () => {
                          await api.post("/dashboard/provisioning/knowledge-draft/approve");
                          toast.success("Knowledge draft approved.");
                        })}
                        data-testid="prov-approve-draft"
                      >Approve draft</Button>
                    </>
                  )}
                  {state.red_flag && (
                    <Button
                      size="sm" variant="outline" disabled={busy}
                      onClick={() => callApi(async () => {
                        await api.post("/dashboard/provisioning/generate-knowledge-draft");
                        toast.success("Retried.");
                      })}
                    >Retry</Button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Q&A approval step */}
          <div className="rounded-xl border bg-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-medium tracking-tight">Step 2 — Proposed Q&amp;A</div>
                <div className="text-xs text-muted-foreground mt-0.5">{approvedQa} of minimum {minQa} approved.</div>
              </div>
              {approvedQa >= minQa
                ? <Badge className="bg-emerald-700 hover:bg-emerald-800 text-white">{approvedQa} approved</Badge>
                : <Badge variant="outline">{qa.rows.length} pending</Badge>}
            </div>
            {qa.rows.length === 0 ? (
              <Button
                disabled={busy || draft?.status !== "approved"}
                onClick={() => callApi(async () => {
                  await api.post("/dashboard/provisioning/generate-qa");
                  toast.success("Proposed Q&A generated.");
                })}
                className="bg-emerald-900 hover:bg-emerald-950 text-white"
                data-testid="prov-generate-qa"
              >
                {draft?.status === "approved" ? "Generate Q&A" : "Approve the draft first"}
              </Button>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {qa.rows.map((row) => (
                  <div key={row.id} className="border rounded-md p-2.5 text-xs space-y-1.5" data-testid={`prov-qa-row-${row.position}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium leading-snug">{row.question}</span>
                      <Badge variant={row.status === "approved" ? "default" : row.status === "rejected" ? "destructive" : "outline"} className="text-[9px] shrink-0">{row.status}</Badge>
                    </div>
                    <p className="text-muted-foreground leading-snug">{row.answer}</p>
                    {row.status !== "approved" && row.status !== "rejected" && (
                      <div className="flex gap-1.5 pt-1">
                        <button
                          disabled={busy}
                          onClick={() => callApi(async () => {
                            await api.post(`/dashboard/provisioning/qa/${row.id}/approve`, {});
                          })}
                          className="text-[10px] px-2 py-0.5 rounded bg-emerald-700 hover:bg-emerald-800 text-white"
                          data-testid={`prov-qa-approve-${row.position}`}
                        >Approve</button>
                        <button
                          disabled={busy}
                          onClick={() => callApi(async () => {
                            await api.post(`/dashboard/provisioning/qa/${row.id}/reject`, {});
                          })}
                          className="text-[10px] px-2 py-0.5 rounded bg-red-100 hover:bg-red-200 text-red-900"
                        >Reject</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Activate */}
          <div className="rounded-xl border-2 border-emerald-900/20 bg-emerald-50/40 p-5">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-4 h-4 text-emerald-900" />
              <div className="font-medium tracking-tight">Step 3 — Activate publicly</div>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Your public chatbot link goes live the moment you activate.</p>
            <Button
              size="lg"
              disabled={busy || state.red_flag || draft?.status !== "approved" || approvedQa < minQa}
              onClick={() => callApi(async () => {
                const r = await api.post("/dashboard/provisioning/activate").then(rr => rr.data);
                toast.success(`AI Employee is live at /chat/${r.slug}`);
              })}
              className="bg-emerald-900 hover:bg-emerald-950 text-white"
              data-testid="prov-activate"
            >
              {state.red_flag ? <><AlertTriangle className="mr-2 w-4 h-4"/> Resolve red-flag first</>
                : draft?.status !== "approved" ? "Approve draft first"
                : approvedQa < minQa ? `Approve ${minQa - approvedQa} more Q&A`
                : <>Activate AI Employee <ChevronRight className="ml-1 w-4 h-4"/></>}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
